'use strict';

/**
 * zone.js manages the different areas on the screen
 * - smallPanel is the panel that opens aside the menu. It can contain some layers
 * - fullPanel is the panel that opens over the site from the edges of the menu and edges of the browser
 * - layer is an element that covers the small panel
 *
 * exports function :
 *  - init
 *  - setSmallPanel
 *  - setFullPanel
 *  - hidePanels
 *  - addLayer
 *  - closeLayer
 *
 */

const jQuery = require('jquery');
const mediator = require('./../../core/mediator');
const translator = require('./../../core/translator');
const container = require('./../../core/container');
const navigatorInfo = require('./../../services/navigator');
const fixHeight = require('./../fixHeight');

const titleTpl = require('./templates/panel_title.hbs');
const layerTpl = require('./templates/panel_layer.hbs');

let $smallPanel;
let $fullPanel;
let transitionName = navigatorInfo.getTransition().end;

const getWrapper = function($panel) {
    return $panel.children('div.cloud-panel-wrapper');
};

/*
* cleanPanel reset content of the layer in parameter and remove layer if found
*/
const cleanPanel = function($panel) {
    let $toolbar = container.get('$toolbar');

    getWrapper($panel).html('');
    $toolbar.find('.cloud-layer-panel-wrapper').remove();
    $toolbar.find('.cloud-panel-footer').remove();
};

const setTitle = function($panel, title, config) {
    if (!title) {
        return;
    }

    let $wrap = getWrapper($panel);
    let $tpl = jQuery(titleTpl({'title': title}));

    config = config || {};

    if (config.extraTitle) {
        $tpl.append(config.extraTitle);
    }

    $wrap.prepend($tpl);
};

const setContent = function($panel, content) {
    let $zone = getWrapper($panel);

    $zone.append(jQuery('<div class="cloud-panel-content"></div>').append(content));
};

const hide = function($panel) {
    if ($panel && !$panel.hasClass('d-none')) {
        $panel.one(transitionName + '.panel', function(event) {
            jQuery(event.currentTarget).removeClass('animating').addClass('d-none');
        });

        $panel.addClass('panel-out');
    }
};

const show = function($panel) {
    if ($panel && $panel.hasClass('d-none')) {
        let showOverlay = jQuery('.cloud-panel').hasClass('d-none');

        if (container.get('isMobile')) {
            jQuery('html').addClass('cloud-no-scroll');
        }

        $panel.addClass('animating').removeClass('d-none');
        setTimeout(function() {
            $panel.removeClass('panel-out');
            mediator.publish('zone:show:before', $panel);
            setTimeout(function() {
                mediator.publish('zone:show:after', $panel);

                if (!container.get('isMobile') && showOverlay) {
                    mediator.publish('show:overlay', {
                        'onClick': hidePanels,
                        'classes': 'panel',
                    });
                }
            }, 15);
        }, 15);
    }
};

/*
* hidePanels : hide small and full panels
*/
const hidePanels = function() {
    if ($smallPanel.find('.cloud-mask-loader').length === 0) {
        hide($fullPanel);
        hide($smallPanel);
        jQuery('html').removeClass('cloud-no-scroll');
        mediator.publish('panels:close');
        mediator.publish('overlay:hide');
    }
};

/*
* closeLayer : closes the given layer in parameter
* @param {String} $layer
*/
const closeLayer = function($layer) {
    $layer.one(transitionName, function(event) {
        jQuery(event.currentTarget).remove();
    });

    hide($fullPanel);

    $layer.addClass('panel-out');
    mediator.publish('layer:close');
};

module.exports.createSubmitButtons = (config = {}) => {
    let onCancelClick = config.onCancelClick || (() => mediator.publish('menu:close'));
    let onSubmitClick = config.onSubmitClick || jQuery.noop;
    let submitLabel = config.submitLabel || 'submit';
    let cancelLabel = config.cancelLabel || 'discard';
    let noCancelBtn = !!config.noCancelBtn;
    let col = noCancelBtn ? 12 : 6;
    let $row = jQuery('<div class="row"></div>');
    let $cancelBtn = jQuery(`<button class="btn btn-default btn-block">${translator.translate(cancelLabel)}</button>`);
    let $submitBtn = jQuery(`<button type="submit" class="btn btn-primary btn-block">${translator.translate(submitLabel)}</button>`);

    $cancelBtn.on(container.get('isMobile') ? 'touchstart MSPointerDown': 'click', onCancelClick);
    $submitBtn.on(container.get('isMobile') ? 'touchstart MSPointerDown': 'click', onSubmitClick);

    if (!noCancelBtn) {
        $row.append(jQuery(`<div class="col-md-${col}"></div>`).append($cancelBtn));
    }

    $row.append(jQuery(`<div class="col-md-${col}"></div>`).append($submitBtn));

    return $row;
};

/*
* setSmallPanel : hide full panel and set title/content of small panel;
* @param {String} title
* @param {String} content
*/
module.exports.setSmallPanel = function(title, content, fix = true, config) {
    hide($fullPanel);

    cleanPanel($smallPanel);
    setTitle($smallPanel, title, config);
    setContent($smallPanel, content);

    if (fix === true) {
        fixHeight.fix($smallPanel.find('.cloud-panel-content'), jQuery(window).height() - 100);
    }

    show($smallPanel);

    return $smallPanel;
};

/*
* addLayer : small panel can contain some layers
*
* @param {String} title
* @param {String} content
*/
const addLayer = function(title, content, config) {
    let $layer = jQuery(layerTpl());
    let $titleTpl = jQuery(titleTpl({'title': title}));

    config = config || {};

    if (config.extraTitle) {
        $titleTpl.append(config.extraTitle);
    }

    $layer.append($titleTpl).append(content);
    $smallPanel.append($layer);
    $layer.find('.btn-close-layout').on('click', closeLayer.bind(undefined, $layer));

    show($layer);

    return $layer;
};

const setFooter = function($zone, html) {
    let $footer;

    if (!($zone instanceof jQuery)) {
        throw new Error('Zone must be an instance of jQuery in ServiceStructure::setFooter');
    }

    if ($zone.find('.cloud-panel-footer').length > 0) {
        $footer = $zone.find('.cloud-panel-footer');
    } else {
        $footer = jQuery('<div class="cloud-panel-footer"></div>');
        $zone.append($footer);
    }

    $footer = $zone.find('.cloud-panel-footer').length > 0 ? $zone.find('.cloud-panel-footer') : jQuery('<div class="cloud-panel-footer"></div>');

    $footer.html(html);
};

/*
* setFullPanel : set the full panel
*
* @param {String} title
* @param {String} content
*/
module.exports.setFullPanel = function(title, content) {
    if (container.get('isMobile')) {
        addLayer(title, content);

        return;
    }

    getWrapper($fullPanel).html('');
    setTitle($fullPanel, title);
    setContent($fullPanel, content);

    show($fullPanel);
    jQuery('html').addClass('cloud-no-scroll');

    return $fullPanel;
};

/*
* init : get the small and full panels; add listener on button close.
*/
module.exports.init = function() {
    $smallPanel = container.get('$smallPanel');
    $fullPanel = container.get('$fullPanel');

    $smallPanel.on('click', 'button.cloud-panel-close', hidePanels);
};

module.exports.hidePanels = hidePanels;
module.exports.addLayer = addLayer;
module.exports.closeLayer = closeLayer;
module.exports.setFooter = setFooter;
